import { io, Socket } from 'socket.io-client';

/**
 * Single socket.io connection shared by every WS hook on the page.
 *
 * Why this exists:
 *  - Each hook (useAutoLikerWebSocket, useAutoCommenterWebSocket,
 *    useAutoSubscriberWebSocket) used to spin up its own `io(...)` with
 *    hard-coded `http://localhost:3001`. In production that means the
 *    browser tries to reach the user's own machine and nothing works.
 *  - socket.io's default path is `/socket.io/`. In production nginx serves
 *    us under `/vkbp/`, so the WebSocket upstream is at `/vkbp/socket.io/`.
 *    We have to tell the client about that explicitly.
 *  - Sharing one socket avoids 3 parallel handshakes per page.
 *
 * The socket is auth'd with the JWT from localStorage; reconnects pick up
 * a refreshed token automatically because we attach `auth.token` via a
 * function.
 */

let socket: Socket | null = null;

/**
 * BASE_URL is the Vite-baked base path (`/vkbp/app/` in prod, `/` in dev).
 * The socket.io upstream however lives one level up from the app —
 * nginx proxies `/vkbp/socket.io/`, not `/vkbp/app/socket.io/` — so we
 * strip both the trailing slash and the `/app` segment:
 *   prod: `/vkbp/socket.io`
 *   dev:  `/socket.io`
 */
const SOCKET_IO_PATH = `${(import.meta.env.BASE_URL || '/')
  .replace(/\/$/, '')
  .replace(/\/app$/, '')}/socket.io`;

export function getSocket(): Socket | null {
  if (typeof window === 'undefined') return null;
  const token = localStorage.getItem('accessToken');
  if (!token) return null;

  if (!socket) {
    // Origin is implicit (same-origin). In dev the Vite proxy forwards
    // /socket.io/ to localhost:3001, in prod nginx forwards /vkbp/socket.io/
    // to backend 3011.
    socket = io({
      path: SOCKET_IO_PATH,
      auth: (cb) => cb({ token: localStorage.getItem('accessToken') }),
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    socket.on('connect', () => {
      // eslint-disable-next-line no-console
      console.log('[ws] connected', socket?.id);
    });
    socket.on('disconnect', (reason) => {
      // eslint-disable-next-line no-console
      console.log('[ws] disconnected:', reason);
    });
    socket.on('connect_error', (error) => {
      // eslint-disable-next-line no-console
      console.error('[ws] connect_error:', error.message);
    });
  }

  return socket;
}

/**
 * Subscribe to the live `connected` status of the shared socket.
 * The callback fires immediately with the current value, then on every change.
 */
export function subscribeConnectionStatus(cb: (connected: boolean) => void): () => void {
  const s = getSocket();
  if (!s) {
    cb(false);
    return () => {};
  }
  cb(s.connected);
  const onConnect = () => cb(true);
  const onDisconnect = () => cb(false);
  s.on('connect', onConnect);
  s.on('disconnect', onDisconnect);
  return () => {
    s.off('connect', onConnect);
    s.off('disconnect', onDisconnect);
  };
}

export function disconnectSocket() {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}
